'use client';

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { Input } from '../../primitives/input';
import { Label } from '../../primitives/label';
import { cn } from '../../utils/cn';

/**
 * UzInput - text input with built-in label, hint, error, leading/trailing
 * adornments, and clearable mode.
 *
 * The base shadcn Input only renders the input element itself. Real forms
 * need surrounding structure (label, error message, hint, decorations), so
 * we wrap once and reuse everywhere.
 */
export interface UzInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Icon or text rendered inside the input, left side. */
  leftAdornment?: ReactNode;
  /** Icon or text rendered inside the input, right side. */
  rightAdornment?: ReactNode;
  /** Wraps the input in a labelled <div> with this classname (for spacing in forms). */
  containerClassName?: string;
}

export const UzInput = forwardRef<HTMLInputElement, UzInputProps>(
  (
    {
      id: providedId,
      label,
      hint,
      error,
      required,
      leftAdornment,
      rightAdornment,
      containerClassName,
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = providedId ?? autoId;
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <Label htmlFor={id} className={cn(disabled && 'opacity-60')}>
            {label}
            {required && (
              <span className="text-destructive ml-1 align-middle text-base leading-none font-semibold">
                *
              </span>
            )}
          </Label>
        )}
        <div className="relative">
          {leftAdornment && (
            <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
              {leftAdornment}
            </span>
          )}
          <Input
            id={id}
            ref={ref}
            disabled={disabled}
            aria-invalid={!!error || undefined}
            aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
            className={cn(
              leftAdornment && 'pl-9',
              rightAdornment && 'pr-9',
              error && 'border-destructive focus-visible:border-destructive',
              className,
            )}
            {...rest}
          />
          {rightAdornment && (
            <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2">
              {rightAdornment}
            </span>
          )}
        </div>
        {error ? (
          <p id={errorId} className="text-destructive text-xs font-medium">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-muted-foreground text-xs">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
UzInput.displayName = 'UzInput';
